import { useRef, useState } from "react";
import client from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

let cache = null;
// Tasks whose sub-task ticks need a comment (linked to a Rates tab that asks for one)
export async function needsTickComment(task) {
  if (!task) return false;
  if (!cache) cache = client.get("/rate-checks/comment-links").then(({ data }) => data).catch(() => ({ task_ids: [], series_ids: [] }));
  const d = await cache;
  return d.task_ids.includes(task.id) || (!!task.series_id && d.series_ids.includes(task.series_id));
}
export const resetTickCommentCache = () => { cache = null; };

// ask(title) resolves to the typed comment, or null if cancelled
export function useTickComment() {
  const [state, setState] = useState(null);
  const [text, setText] = useState("");
  const res = useRef(null);
  const ask = (title) => new Promise((resolve) => { res.current = resolve; setText(""); setState({ title }); });
  const close = (val) => { res.current?.(val); res.current = null; setState(null); };
  const dialog = (
    <Dialog open={!!state} onOpenChange={(o) => !o && close(null)}>
      <DialogContent className="max-w-sm" data-testid="tick-comment-dialog">
        <DialogHeader><DialogTitle className="font-heading text-base">Comment for {state?.title}</DialogTitle></DialogHeader>
        <textarea autoFocus value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="e.g. 12 Months"
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); close(text.trim()); } }}
          className="w-full rounded-md border border-slate-200 p-2.5 text-sm focus:border-sky-400 focus:outline-none" data-testid="tick-comment-input" />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => close(null)} data-testid="tick-comment-cancel">Cancel</Button>
          <Button size="sm" onClick={() => close(text.trim())} className="bg-sky-600 hover:bg-sky-700" data-testid="tick-comment-save">Tick off</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
  return { ask, dialog };
}

// Returns the new sub-task list with the comment applied, or null if the user cancelled
export async function withTickComment(task, oldSubs, newSubs, ask) {
  const old = Object.fromEntries((oldSubs || []).map((s) => [s.id, s]));
  const ticked = newSubs.find((s) => s.completed && old[s.id] && !old[s.id].completed);
  if (!ticked || !(await needsTickComment(task))) return newSubs;
  const c = await ask(ticked.title);
  if (c === null) return null;
  return newSubs.map((s) => (s.id === ticked.id ? { ...s, comment: c } : s));
}
